/**
 * @fileoverview The ONE web-vitals collector (UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-13).
 *
 * Measured 2026-09-25: Core Web Vitals were collected NOWHERE in the fleet. `@puffin-core/
 * marketing-shell` carries `wireWebVitals` and `<WebVitalsTracker>`, but both take the library as
 * an injected dependency and no caller ever injects it — no workspace declared `web-vitals` at all
 * (it sat in the lockfile only as a private dependency of `@firebase/performance`). The nine
 * marketing entries each hand-rolled a `PerformanceObserver` that reported raw entry TIMESTAMPS as
 * exceptions to a reporter with no transport.
 *
 * This collector owns the library, so no app wires it, and sends what it measures through the ONE
 * browser → ingest pipeline (`ServerTransport.enqueue`). The ingest is a log ingest, so a vital
 * travels as an INFO record — `Web vital LCP`, `data: { vital, value, rating, delta,
 * navigationType, id }` — which is exactly what `@puffin-core/client-log-routes` accepts.
 *
 * Three rules decide whether a measured vital leaves the page:
 *
 * 1. **Sampling is the page trace's decision** (CLIENT-LOG-SAMPLING-001), applied by the SINK
 *    `createAppLogger` builds, never re-rolled here: a sampled page sends its vitals with its INFO
 *    lines and its spans, an unsampled one sends none. One knob, `VITE_TRACING_SAMPLING_RATIO`.
 * 2. **Consent governs a consent-governed page** (`./telemetryConsent.js`): nothing is sent until
 *    the visitor grants it; a decline drops what was held; a PENDING decision (the banner is up,
 *    unanswered) keeps holding, so a first-visit grant still releases TTFB and FCP (F-SG-2).
 * 3. **Nothing leaves before the page's posture can be known.** FCP and TTFB are measured before
 *    React has mounted the consent provider that would govern the page, so every vital is HELD
 *    until the posture is decided — a grant releases the held vitals at once; the first HIDE
 *    releases them on an ungoverned page (the authenticated apps) and drops them on a governed
 *    page without a grant (pending or declined). The hide release runs through the transport's `beforeHide` hook, after
 *    `web-vitals` has reported the final LCP / CLS / INP from the same event and before the beacon
 *    that carries them.
 *
 * The collector is fail-soft, because telemetry must never be the thing that breaks a page: it
 * installs only where the Performance Timeline exists (`PerformanceObserver` +
 * `performance.getEntriesByType`, which `web-vitals` calls unguarded — jsdom has neither), and a
 * registrar that throws anyway is reported through `onError` and skipped, never propagated into the
 * bootstrap that called it.
 *
 * @module collectWebVitals
 */

import {
  TELEMETRY_CONSENT,
  getTelemetryConsent as defaultGetConsent,
  onTelemetryConsentChange as defaultOnConsentChange,
} from './telemetryConsent.js';

/** The Core Web Vitals and their two diagnostic companions, in `web-vitals`' registration names. */
export const WEB_VITAL_REGISTRARS = Object.freeze(['onCLS', 'onFCP', 'onINP', 'onLCP', 'onTTFB']);

/** More than a page can legitimately produce before its first hide (five, plus bfcache restores). */
const MAX_HELD = 20;

/** One installation per page: a second bootstrap (HMR, a test) detaches the first. */
let activeUninstall = null;

/**
 * The record a vital becomes. CLS is a unitless score (four decimals); every other vital is a
 * duration in milliseconds, sent whole.
 *
 * @param {{name: string, value: number, delta: number, rating: string, navigationType: string, id: string}} metric
 * @returns {{message: string, data: Object}}
 */
export function toVitalRecord(metric) {
  const round = (n) => (metric.name === 'CLS' ? Number(n.toFixed(4)) : Math.round(n));
  return {
    message: `Web vital ${metric.name}`,
    data: {
      vital: metric.name,
      value: round(metric.value),
      delta: round(metric.delta),
      rating: metric.rating,
      navigationType: metric.navigationType,
      id: metric.id,
    },
  };
}

/**
 * Whether a host can measure web vitals at all.
 * @param {Object|null} host - the window
 * @returns {boolean}
 */
export function supportsWebVitals(host) {
  return (
    typeof host?.PerformanceObserver === 'function' &&
    typeof host?.performance?.getEntriesByType === 'function'
  );
}

/**
 * Observe the web vitals and release each through `ship` under the three rules above.
 *
 * @param {Object} options
 * @param {Object|null} options.library - the `web-vitals` module (`onCLS`, …); `null` disables
 * @param {(metric: Object) => void} options.ship - where a released vital goes
 * @param {(hook: () => void) => (() => void)} options.beforeHide - registers the hide release
 *   (the transport's `beforeHide`)
 * @param {{get: Function, subscribe: Function}} [options.consent] - the posture source; defaults to
 *   the page singleton in `./telemetryConsent.js`
 * @param {Object|null} [options.host=globalThis] - the window whose Performance Timeline is read
 * @param {(registrar: string, err: *) => void} [options.onError] - told when a registrar throws
 * @returns {() => void} uninstall — later reports are ignored (`web-vitals` has no unsubscribe)
 * @throws {TypeError} when `ship` or `beforeHide` is missing
 */
export function installWebVitalsCollector({
  library,
  ship,
  beforeHide,
  consent = { get: defaultGetConsent, subscribe: defaultOnConsentChange },
  host = globalThis,
  onError = () => {},
} = {}) {
  if (typeof ship !== 'function' || typeof beforeHide !== 'function') {
    throw new TypeError('installWebVitalsCollector requires `ship` and `beforeHide` functions');
  }
  if (activeUninstall) activeUninstall();
  if (!library || !supportsWebVitals(host)) {
    activeUninstall = null;
    return () => {};
  }

  let detached = false;
  let openAfterHide = false;
  let held = [];

  const hold = (metric) => {
    held.push(metric);
    if (held.length > MAX_HELD) held.shift();
  };

  const release = () => {
    const pending = held;
    held = [];
    for (const metric of pending) ship(metric);
  };

  const onMetric = (metric) => {
    if (detached) return;
    const { governed, decision } = consent.get();
    if (governed) {
      if (decision === TELEMETRY_CONSENT.GRANTED) ship(metric);
      else if (decision === TELEMETRY_CONSENT.PENDING) hold(metric);
      return;
    }
    if (openAfterHide) {
      ship(metric);
      return;
    }
    hold(metric);
  };

  const onHide = () => {
    const { governed, decision } = consent.get();
    if (governed && decision !== TELEMETRY_CONSENT.GRANTED) {
      held = [];
      return;
    }
    if (!governed) openAfterHide = true;
    release();
  };

  const onConsent = ({ governed, decision }) => {
    if (!governed) return;
    if (decision === TELEMETRY_CONSENT.GRANTED) release();
    else if (decision === TELEMETRY_CONSENT.DECLINED) held = [];
  };

  const removeHideHook = beforeHide(onHide);
  const unsubscribe = consent.subscribe(onConsent);
  for (const registrar of WEB_VITAL_REGISTRARS) {
    if (typeof library[registrar] !== 'function') continue;
    try {
      library[registrar](onMetric);
    } catch (err) {
      onError(registrar, err);
    }
  }

  const uninstall = () => {
    detached = true;
    held = [];
    removeHideHook();
    unsubscribe();
    if (activeUninstall === uninstall) activeUninstall = null;
  };
  activeUninstall = uninstall;
  return uninstall;
}
